import type { ButtonBaseProps } from '@mui/material'
import { ButtonBase } from '@mui/material'
import { uniqBy } from 'lodash-es'
import type { FC } from 'react'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import SolarAltArrowDownBold from '~icons/solar/alt-arrow-down-bold'
import { createTagPageUrl } from 'src/components/Tag/utils'
import type { Tag } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'

export interface ClickableTagProps extends ButtonBaseProps<any> {
  name: string
}

export const ClickableTag: FC<ClickableTagProps> = ({
  name,
  className,
  children = (
    <h2>
      #<span className="font-bold">{name}</span>
    </h2>
  ),
  ...props
}) => {
  return (
    <ButtonBase
      className={cx(
        'px-2 py-1 rounded-full transition active:scale-90 focus:bg-active',
        className,
      )}
      {...props}
    >
      {children}
    </ButtonBase>
  )
}

type TagListProps = {
  tags: Pick<Tag, 'id' | 'name' | 'displayName'>[]
  type?: 'model' | 'artwork'
}

const TagList = forwardRef<HTMLDivElement, TagListProps & StyleProps>(
  function TagList(props, ref) {
    const { tags, type, className } = props
    return (
      <div
        ref={ref}
        className={cx(
          'flex flex-wrap gap-1 dark:text-white text-sm text-neutral-600',
          className,
        )}
      >
        {uniqBy(tags, t => t.id).map(({ name, displayName, id }) => {
          return (
            <ClickableTag
              to={type ? createTagPageUrl(type, name) : undefined}
              key={id}
              target="_blank"
              name={displayName ?? name}
              component={Link}
              className="bg-background-light"
            />
          )
        })}
      </div>
    )
  },
)

export default TagList

export function ExpandableTagList(props: TagListProps) {
  const ref = useRef<HTMLDivElement>(null!)
  const [isExpanded, setIsExpanded] = useState(true)
  const [isExpandable, setIsExpandable] = useState(false)

  useEffect(() => {
    setIsExpandable(ref.current.clientHeight > 28) // hardcoded
    setTimeout(() => {
      setIsExpanded(false)
    }, 0)
  }, [props.tags.length])

  return (
    <div className="relative">
      <TagList
        className={cx(
          isExpandable && 'pr-8',
          isExpanded ? '' : 'h-[28px] overflow-hidden',
        )}
        ref={ref}
        {...props}
      />
      {isExpandable && (
        <button
          className="absolute right-0 top-0 rounded-full px-2 dark:text-white text-neutral-600 bg-[#EEE] dark:bg-[#58585A] w-min h-[28px] flex items-center justify-center"
          onClick={() => {
            setIsExpanded(prev => !prev)
          }}
        >
          <SolarAltArrowDownBold
            className={cx('text-sm transition', isExpanded && 'rotate-180')}
          />
        </button>
      )}
    </div>
  )
}
